import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface GitCommand {
  cmd: string;
  description: string;
  icon: string;
  color: string;
  branch: string;
}

const COMMANDS: GitCommand[] = [
  { cmd: 'git init', description: '初始化仓库，创建 .git 目录', icon: '🏗️', color: '#4ECDC4', branch: 'main' },
  { cmd: 'git add .', description: '将工作区更改添加到暂存区', icon: '📦', color: '#45B7AF', branch: 'main' },
  { cmd: 'git commit', description: '将暂存区快照保存为新提交', icon: '💾', color: '#667EEA', branch: 'main' },
  { cmd: 'git branch feature', description: '从当前位置创建新分支', icon: '🌿', color: '#2ECC71', branch: 'feature' },
  { cmd: 'git merge', description: '将分支合并回主线', icon: '🔀', color: '#FF6B35', branch: 'main' },
  { cmd: 'git push', description: '将本地提交推送到远程仓库', icon: '🚀', color: '#9B59B6', branch: 'origin' },
];

const ZONES = [
  { name: '工作区', nameEn: 'Working Dir', color: '#FF6B35', icon: '📝' },
  { name: '暂存区', nameEn: 'Staging', color: '#FFD700', icon: '📋' },
  { name: '本地仓库', nameEn: 'Local Repo', color: '#4ECDC4', icon: '💻' },
  { name: '远程仓库', nameEn: 'Remote', color: '#667EEA', icon: '☁️' },
];

export const GitWorkflowAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentCmdIndex = Math.min(
    Math.floor(Math.max(0, frame - 70) / 75),
    COMMANDS.length - 1,
  );

  return (
    <AbsoluteFill
      style={{
        background: '#0D1117',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 55,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #F05033, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Git 工作流详解
        </h1>
        <p style={{ fontSize: 26, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Git Workflow · 版本控制的核心
        </p>
      </div>

      {/* Four zones */}
      <div
        style={{
          position: 'absolute',
          top: 190,
          left: 55,
          right: 55,
          display: 'flex',
          gap: 20,
        }}
      >
        {ZONES.map((zone, i) => {
          const zoneProg = spring({
            frame: frame - 15 - i * 8,
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const arrowProg = interpolate(frame, [40 + i * 10, 55 + i * 10], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });

          return (
            <React.Fragment key={zone.nameEn}>
              <div
                style={{
                  flex: 1,
                  padding: '20px 16px',
                  background: `${zone.color}10`,
                  border: `2px solid ${zone.color}35`,
                  borderRadius: 16,
                  textAlign: 'center',
                  opacity: zoneProg,
                  transform: `scale(${zoneProg})`,
                }}
              >
                <div style={{ fontSize: 36 }}>{zone.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: zone.color, marginTop: 6 }}>
                  {zone.name}
                </div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                  {zone.nameEn}
                </div>
              </div>
              {i < ZONES.length - 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 28,
                    color: 'rgba(255,255,255,0.3)',
                    opacity: arrowProg,
                  }}
                >
                  →
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Command timeline */}
      <div
        style={{
          position: 'absolute',
          top: 360,
          left: 55,
          right: 55,
          bottom: 80,
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 24,
            top: 0,
            bottom: 0,
            width: 3,
            background: 'linear-gradient(180deg, #667EEA 0%, #667EEA44 100%)',
            borderRadius: 2,
          }}
        />

        {COMMANDS.map((cmd, i) => {
          const cmdStart = 70 + i * 75;
          const prog = spring({
            frame: frame - cmdStart,
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          const isActive = i === currentCmdIndex && frame >= cmdStart;
          const y = i * 115;

          return (
            <div
              key={cmd.cmd}
              style={{
                position: 'absolute',
                left: 0,
                top: y,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 24,
                opacity: prog,
                transform: `translateX(${interpolate(prog, [0, 1], [40, 0])}px)`,
              }}
            >
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: isActive ? cmd.color : `${cmd.color}30`,
                  border: `3px solid ${cmd.color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  flexShrink: 0,
                  boxShadow: isActive ? `0 0 20px ${cmd.color}60` : 'none',
                }}
              >
                {cmd.icon}
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  background: isActive ? `${cmd.color}15` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isActive ? cmd.color + '50' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: 14,
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                }}
              >
                <code
                  style={{
                    fontSize: 26,
                    fontWeight: 700,
                    color: cmd.color,
                    fontFamily: 'Menlo, Consolas, monospace',
                    minWidth: 280,
                  }}
                >
                  $ {cmd.cmd}
                </code>
                <span style={{ fontSize: 22, color: '#D1D5DB' }}>{cmd.description}</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    padding: '4px 14px',
                    background: `${cmd.color}20`,
                    borderRadius: 8,
                    fontSize: 16,
                    color: cmd.color,
                    fontWeight: 600,
                  }}
                >
                  {cmd.branch}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 30,
          right: 55,
          fontSize: 24,
          color: 'rgba(255,255,255,0.25)',
        }}
      >
        分布式版本控制 · 团队协作利器
      </div>
    </AbsoluteFill>
  );
};
